import React from 'react';
import { DateTime } from 'src_common/utils/time/time';
import { money } from 'src/utils/format';
import { observer } from 'src/utils/mobx-react';
import {
    TableWrapper,
    TableHeaderRow,
    TableRow,
    ColumnHeader,
    TableCell,
    SectionMessage,
} from 'src/components/FraudAndCompliance/FraudAndCompliancesTab/FraudAndCompliances/components/PaymentDeclines.style';
import { ChevronDownIconWrapper, Select, SelectWrapper } from './CommonTable.styles';
import { CustomerButton } from 'src/components/FraudAndCompliance/CustomerButton/CustomerButton';
import { useAppStateContext } from 'src/AppState/AppState';

const ACTIONS = {
    your: [
        { action: 'cancel', name: 'Mark as reviewed' },
        { action: 'unassign', name: 'Unassign' },
    ],
    none: [{ action: 'assign', name: 'Assign to me' }],
    other: [{ action: 'unassign', name: 'Unassign' }],
};
type ActionsType = 'your' | 'none' | 'other';

interface OptionType {
    action: string;
    name: string;
}
const getOption = (state: OptionType, id: string): React.ReactElement => {
    return (
        <option
            key={`${id}-${state.action}`}
            value={state.action}
        >
            {state.name}
        </option>
    );
};
interface DataListType {
    accountId: number;
    accountName: string;
    channels: Array<string>;
    createdAt: string;
    details: {
        accountId: string;
        amount: number;
        'transaction-id': number;
    };
    id: string;
    status: string;
    type: string;
    universeId: string;
}

type DataType = Immutable.Map<unknown, unknown> | Immutable.List<DataListType>;
interface PropsType {
    type: ActionsType;
    status: string;
    className: string;
    onCancelRequest: (matchedAccount: unknown) => void;
    onAssignRequest: (matchedAccount: unknown) => void;
    onUnassignRequest: (matchedAccount: unknown) => void;
    onOpenAccountRequest: (id: number, name: string) => void;
    data: DataType;
}

export const PaymentDeclines = observer('PaymentDeclines', (props: PropsType): React.ReactElement | null => {
    const { models } = useAppStateContext();

    //@ts-expect-error
    const doAction = (e, paymentDeclined): void => {
        const action = e.target.value;

        const { onCancelRequest, onAssignRequest, onUnassignRequest } = props;
        if (action === 'unassign') {
            onUnassignRequest(paymentDeclined);
        } else if (action === 'assign') {
            onAssignRequest(paymentDeclined);
        } else if (action === 'cancel') {
            onCancelRequest(paymentDeclined);
        }
    };

    //@ts-expect-error
    const renderRow = (paymentDeclined, type: ActionsType): React.ReactNode => {
        const id = paymentDeclined.get('id');
        return (
            <TableRow
                key={id}
                section={type}
                data-test={`payment-declines-row-${paymentDeclined.getIn(['details', 'transaction-id'])}`}
            >
                <TableCell data-test='payment-declines-date-detected'>
                    {DateTime.from(paymentDeclined.get('createdAt'))?.format('DD-MM-YYYY HH:mm') ?? '-'}
                </TableCell>
                <TableCell data-test='payment-declines-customer'>
                    <CustomerButton
                        models={models}
                        customerId={paymentDeclined.get('accountId')}
                    />
                </TableCell>
                <TableCell data-test='payment-declines-transaction-id'>
                    {paymentDeclined.getIn(['details', 'transaction-id'])}
                </TableCell>
                <TableCell data-test='payment-declines-transaction-type'>
                    {paymentDeclined.getIn(['details', 'transactionType'])}
                </TableCell>
                <TableCell data-test='payment-declines-transaction-status'>
                    {paymentDeclined.getIn(['details', 'transactionStatus'])}
                </TableCell>
                <TableCell data-test='payment-declines-card-details'>
                    {paymentDeclined.getIn(['details', 'cardNumber']) === undefined
                        ? null
                        : `**** ${paymentDeclined.getIn(['details', 'cardNumber']).slice(-4)}`}
                </TableCell>
                <TableCell data-test='payment-declines-card-expiry-date'>
                    {paymentDeclined.getIn(['details', 'expiryDate']) === ''
                        ? null
                        : DateTime.from(paymentDeclined.getIn(['details', 'expiryDate']))?.format('MM/YY')}
                </TableCell>
                <TableCell data-test='payment-declines-amount'>
                    {money(paymentDeclined.getIn(['details', 'amount']))}
                </TableCell>
                <TableCell disableColor={true}>
                    <SelectWrapper>
                        <Select
                            name='action'
                            onChange={(e: React.SyntheticEvent): void => doAction(e, paymentDeclined)}
                            data-test='payment-declines-action-form'
                        >
                            <option>Choose an action</option>
                            {ACTIONS[type].map((state) => getOption(state, id))}
                        </Select>
                        <ChevronDownIconWrapper />
                    </SelectWrapper>
                </TableCell>
            </TableRow>
        );
    };

    const { data, type, status } = props;
    const areNoItems = status === 'FINISHED' && data.size === 0;

    return (
        <div>
            {areNoItems ? (
                <SectionMessage>There are no items</SectionMessage>
            ) : (
                <TableWrapper>
                    <thead>
                        <TableHeaderRow>
                            <ColumnHeader>Date Detected</ColumnHeader>
                            <ColumnHeader>Customer</ColumnHeader>
                            <ColumnHeader>Transaction ID</ColumnHeader>
                            <ColumnHeader>Transaction Type</ColumnHeader>
                            <ColumnHeader>Transaction Status</ColumnHeader>
                            <ColumnHeader>Payment Details</ColumnHeader>
                            <ColumnHeader>Expiry Date</ColumnHeader>
                            <ColumnHeader>Amount</ColumnHeader>
                            <ColumnHeader>Action</ColumnHeader>
                        </TableHeaderRow>
                    </thead>
                    <tbody>{data.map((d) => renderRow(d, type)).toArray()}</tbody>
                </TableWrapper>
            )}
        </div>
    );
});
